<template>
  <v-layout
    align-center
    class="playposit-audio-player"
    :style="{
      '--buffered-width': bufferedWidth,
    }"
  >
    <audio
      @progress="handleAudioProgress"
      @ended="playerState = 'end'"
      @timeupdate="updateSeekSlider"
      @loadedmetadata="updateAudioDuration"
      ref="audioElement"
      class="responsive"
      :src="src"
    />
    <v-flex
      xs2
      class="text-xs-center"
    >
      <v-btn
        @click="togglePlayPause"
        flat
        :ripple="false"
        color="white"
        icon
        small
      >
        <v-icon>
          {{ playerState === 'play' ? 'pause' : 'play_arrow' }}
        </v-icon>
      </v-btn>
    </v-flex>
    <v-flex
      xs8
    >
      <v-layout
        column
        justify-center
        class="seek-slider-layout"
      >
        <input
          @input="handleSeekSliderInput"
          @change="handleSeekSliderChange"
          ref="seekSlider"
          type="range"
          :value="currentTime"
          class="playposit-range-input full-width"
          :max="maxSeekSliderValue"
          :min="0"
          step="any"
        >
        <v-layout
          align-center
          justify-space-between
          class="caption playposit-audio-player-displayed-durations full-width"
        >
          <div class="white--text">{{ displayedCurrentTime }}</div>
          <div class="white--text">{{ displayedAudioDuration }}</div>
        </v-layout>
      </v-layout>
    </v-flex>
    <v-flex
      xs2
      class="playposit-audio-player-volume-column"
    >
      <v-layout
        @mouseenter="isHoveringWithinVolumePanel = true;"
        @mouseover="isHoveringWithinVolumePanel = true;"
        @mouseleave="isHoveringWithinVolumePanel = false;"
        column
        justify-center
        align-center
      >
        <v-btn
          @click="toggleMute"
          @focus="isFocusedWithinVolumePanel = true"
          @focusout="handleVolumePanelBlur($event, 'button')"
          icon
          small
          class="volume-button"
        >
          <v-icon color="white">
            {{ volumeButtonIcon }}
          </v-icon>
        </v-btn>
        <transition name="fade">
          <input
            v-if="shouldShowVolumeSlider"
            @focus="isFocusedWithinVolumePanel = true"
            @focusout="handleVolumePanelBlur($event, 'slider')"
            type="range"
            class="volume-slider playposit-range-input minimal full-width"
            ref="volumeSlider"
            v-model.number="volume"
            :max="1"
            :min="0"
            :step="0.01"
          >
        </transition>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'PlaypositAudioPlayer',
  props: {
    src: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    playerState: 'pause',
    audioDuration: 0,
    currentTime: 0,
    volume: 0.5,
    isFocusedWithinVolumePanel: false,
    isHoveringWithinVolumePanel: false,
    isVideoMuted: false,
    bufferedWidth: '0%',
  }),
  computed: {
    shouldShowVolumeSlider() {
      return (
        this.isFocusedWithinVolumePanel
        || this.isHoveringWithinVolumePanel
      );
    },
    displayedAudioDuration() {
      const number = this.audioDuration < 1 ? 1 : this.audioDuration;
      return number.toHoursMinutesSeconds();
    },
    displayedCurrentTime() {
      return this.currentTime.toHoursMinutesSeconds();
    },
    maxSeekSliderValue() {
      const floorDuration = Math.floor(this.audioDuration);
      return floorDuration < 1 ? 1 : floorDuration;
    },
    volumeButtonIcon() {
      if (this.isVideoMuted) return 'volume_off';
      else if (this.volume > 0.75) return 'volume_up';
      else if (this.volume > 0.25) return 'volume_down';
      return 'volume_mute';
    },
  },
  methods: {
    handleAudioProgress() {
      const bufferedAmount = Math.floor(this.$refs.audioElement.buffered.end(this.$refs.audioElement.buffered.length - 1));
      const result = `${(bufferedAmount / this.maxSeekSliderValue) * 100}%`;
      this.bufferedWidth = result === 'Nan%' ? '0%' : result;
    },
    handleVolumePanelBlur(event) {
      if (!event?.relatedTarget?.closest('.playposit-audio-player-volume-column')) {
        this.isFocusedWithinVolumePanel = false;
      }
    },
    updateAudioDuration() {
      if (_.isFinite(this.$refs.audioElement.duration)) this.audioDuration = this.$refs.audioElement.duration;
    },
    togglePlayPause() {
      if (this.playerState === 'play') {
        this.$refs.audioElement.pause();
        this.playerState = 'pause';
      } else {
        if (
          this.playerState === 'end'
          && this.currentTime === this.maxSeekSliderValue
        ) this.currentTime = 0;
        this.$refs.audioElement.play();
        this.playerState = 'play';
      }
    },
    handleSeekSliderChange() {
      this.$refs.audioElement.currentTime = parseFloat(this.$refs.seekSlider.value);
    },
    handleSeekSliderInput: _.throttle(function handleSeekSliderInput() {
      this.currentTime = parseFloat(this.$refs.seekSlider.value);
    }, 100, {
      leading: true,
      trailing: true,
    }),
    updateSeekSlider() {
      this.currentTime = parseFloat(this.$refs.audioElement.currentTime);
    },
    toggleMute() {
      this.isVideoMuted = !this.isVideoMuted;
    },
  },
  watch: {
    volume(newValue) {
      this.$refs.audioElement.volume = newValue;
    },
    isVideoMuted(newValue) {
      this.$refs.audioElement.muted = newValue;
    },
  },
  async mounted() {
    await this.$nextTick();
    if (this.$refs.audioElement.readyState > 0) this.updateAudioDuration();
  },
};
</script>

<style lang="scss">
.playposit-audio-player {
  .playposit-audio-player-volume-column > div {
    position: relative;
  }

  .seek-slider-layout {
    position: relative;
    top: 9px;
  }

  .playposit-audio-player-displayed-durations {
    position: relative;
    top: -8px;
  }

  .volume-slider {
    width: 65% !important;
    position: absolute !important;
    top: 65% !important;

    .v-slider__thumb {
      height: 12px;
      width: 12px;
      left: -6px;
    }

    .v-slider__track {
      left: 0 !important;
    }
  }

  .playposit-range-input {
    -webkit-appearance: none;
    margin: 12px 0;
    width: 100%;
  }

  .playposit-range-input {
    outline: none !important;
  }

  .playposit-range-input::-webkit-slider-runnable-track {
    width: 100%;
    height: 2px;
    cursor: pointer;
    background: linear-gradient(to right, #D9D9D9 var(--buffered-width), #B3B3B3 var(--buffered-width));
  }

  .playposit-range-input::-webkit-slider-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #FFF;
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -7px;
    transition: transform 0.1s ease-in-out;
    transform: scale(0.9);
  }

  .playposit-range-input.minimal::-webkit-slider-thumb {
    height: 8px;
    width: 8px;
    margin-top: -3px;
  }

  .playposit-range-input:active::-webkit-slider-thumb {
    transform: scale(1.1);
  }

  .playposit-range-input::-moz-range-track {
    width: 100%;
    height: 2px;
    cursor: pointer;
    background: linear-gradient(to right, #D9D9D9 var(--buffered-width), #B3B3B3 var(--buffered-width));
  }

  .playposit-range-input::-moz-range-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #FFF;
    cursor: pointer;
    margin-top: -7px;
    transition: transform 0.1s ease-in-out;
    transform: scale(0.9);
  }

  .playposit-range-input.minimal::-moz-range-thumb {
    height: 8px;
    width: 8px;
    margin-top: -3px;
  }

  .playposit-range-input:active::-moz-range-thumb {
    transform: scale(1.1);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }
  .fade-enter,
  .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0 !important;
  }
}
</style>